import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, AlertIcon, Badge, Box, Button, Center, Checkbox, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
  DrawerFooter, DrawerHeader, DrawerOverlay, Flex, FormControl, FormHelperText, FormLabel, HStack, Input, InputGroup,
  InputLeftAddon, InputRightAddon, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Select, SimpleGrid, Spinner, Switch, Table, Tbody, Td, Text, Th, Thead, Tr, VStack, Wrap, WrapItem,
  useToast,
} from "@chakra-ui/react";
import { Pencil, Play, Plus, Trash2, FlaskConical } from "lucide-react";
import ConfirmDialog from "../ConfirmDialog";
import { aplicarMlAdsRegra, excluirMlAdsRegra, salvarMlAdsRegra, simularMlAdsRegra } from "../../services/api";
import {
  ACCENT, MOTIVOS_IGNORADOS, ROAS_EQUILIBRIO, Vazio, brl, dataHoraBR, erroMsg, num, pct, roasTxt,
} from "./comum";

const vazioOuNumero = (v) => (v === null || v === undefined ? "" : String(v));

function regraVazia(opcoes) {
  const padrao = opcoes?.padrao_saida || {};
  const saida = {};
  Object.keys(padrao).forEach((k) => { saida[k] = vazioOuNumero(padrao[k]); });
  return {
    id: null,
    nome: "",
    ativa: true,
    simulacao: true,
    prioridade: 100,
    campanhas: {},
    criterios_entrada: {
      fornecedor_ids: [], estoque_taga_min: "", estoque_fornecedor_min: "", preco_min: "", preco_max: "", margem_min_pct: "",
    },
    criterios_saida: saida,
  };
}

function regraParaForm(r) {
  const e = r.criterios_entrada || {};
  const s = r.criterios_saida || {};
  const saida = {};
  Object.keys(s).forEach((k) => { saida[k] = vazioOuNumero(s[k]); });
  return {
    id: r.id,
    nome: r.nome,
    ativa: !!r.ativa,
    simulacao: !!r.simulacao,
    prioridade: r.prioridade ?? 100,
    campanhas: { ...(r.campanhas || {}) },
    criterios_entrada: {
      fornecedor_ids: e.fornecedor_ids || [],
      estoque_taga_min: vazioOuNumero(e.estoque_taga_min),
      estoque_fornecedor_min: vazioOuNumero(e.estoque_fornecedor_min),
      preco_min: vazioOuNumero(e.preco_min),
      preco_max: vazioOuNumero(e.preco_max),
      margem_min_pct: vazioOuNumero(e.margem_min_pct),
    },
    criterios_saida: saida,
  };
}

// Campo vazio = critério desligado (o backend grava null).
const paraEnvio = (form) => {
  const limpar = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === "" ? null : v]));
  return {
    ...form,
    campanhas: Object.fromEntries(Object.entries(form.campanhas).filter(([, v]) => v)),
    criterios_entrada: limpar(form.criterios_entrada),
    criterios_saida: limpar(form.criterios_saida),
  };
};

function chipsEntrada(e, fornecedores) {
  const chips = [];
  if (e?.fornecedor_ids?.length) {
    const nomes = e.fornecedor_ids.map((id) => fornecedores.find((f) => f.id === id)?.nome || `#${id}`);
    chips.push(`Fornecedor: ${nomes.join(", ")}`);
  }
  if (e?.estoque_taga_min !== null && e?.estoque_taga_min !== undefined) chips.push(`Estoque TAGA ≥ ${num(e.estoque_taga_min)}`);
  if (e?.estoque_fornecedor_min !== null && e?.estoque_fornecedor_min !== undefined) chips.push(`Estoque fornecedor ≥ ${num(e.estoque_fornecedor_min)}`);
  if (e?.preco_min !== null && e?.preco_min !== undefined) chips.push(`Preço ≥ ${brl(e.preco_min)}`);
  if (e?.preco_max !== null && e?.preco_max !== undefined) chips.push(`Preço ≤ ${brl(e.preco_max)}`);
  if (e?.margem_min_pct !== null && e?.margem_min_pct !== undefined) chips.push(`Margem ≥ ${num(e.margem_min_pct, 1)}%`);
  return chips;
}

function chipsSaida(s) {
  const chips = [];
  if (s?.roas_min !== null && s?.roas_min !== undefined) chips.push(`ROAS < ${roasTxt(s.roas_min)} (≥ ${num(s.min_cliques)} cliques)`);
  if (s?.ctr_min_pct !== null && s?.ctr_min_pct !== undefined) chips.push(`CTR < ${pct(s.ctr_min_pct)} (≥ ${num(s.min_impressoes_ctr)} impr.)`);
  if (s?.gasto_sem_venda_max !== null && s?.gasto_sem_venda_max !== undefined) chips.push(`${brl(s.gasto_sem_venda_max)} sem venda`);
  chips.push(`janela ${num(s?.janela_dias)}d · carência ${num(s?.carencia_dias)}d · espera ${num(s?.cooldown_dias)}d`);
  return chips;
}

function nomeCampanha(opcoes, conta, id) {
  const c = (opcoes?.contas || []).find((x) => String(x.id) === String(conta));
  const cp = (c?.campanhas || []).find((x) => String(x.id) === String(id));
  return cp ? `${cp.nome}${cp.status === "paused" ? " (pausada)" : ""}` : `#${id} (não encontrada)`;
}

function ResumoConta({ conta, r, opcoes }) {
  if (!r) return null;
  if (r.erro && !r.campanha) {
    return <Alert status="error" fontSize="sm" borderRadius="md"><AlertIcon />Conta {conta}: {r.erro}</Alert>;
  }
  const ignorados = Object.entries(r.ignorados || {}).filter(([, n]) => n > 0);
  return (
    <Box>
      <Text fontSize="sm" fontWeight="bold">
        Conta {conta} → {r.campanha?.nome || nomeCampanha(opcoes, conta, r.campanha?.id)}
        {r.campanha?.status === "paused" ? " (pausada)" : ""}
      </Text>
      <Wrap spacing={2} mt={1}>
        <WrapItem><Badge>{num(r.candidatos)} atendem a entrada</Badge></WrapItem>
        <WrapItem><Badge colorScheme="blue">{num(r.na_campanha)} na campanha</Badge></WrapItem>
        <WrapItem><Badge colorScheme="green">{num(r.entrar)} entram</Badge></WrapItem>
        <WrapItem><Badge colorScheme="red">{num(r.sair)} saem{r.sair_desempenho ? ` (${num(r.sair_desempenho)} por desempenho)` : ""}</Badge></WrapItem>
        {r.sem_ads > 0 && <WrapItem><Badge colorScheme="yellow">{num(r.sem_ads)} ainda sem grupo no Ads</Badge></WrapItem>}
        {r.a_mapear > 0 && <WrapItem><Badge colorScheme="yellow">{num(r.a_mapear)} a mapear</Badge></WrapItem>}
        {r.executados > 0 && <WrapItem><Badge colorScheme="green" variant="solid">{num(r.executados)} feitos</Badge></WrapItem>}
        {r.falhas > 0 && <WrapItem><Badge colorScheme="red" variant="solid">{num(r.falhas)} falharam</Badge></WrapItem>}
        {r.limite_atingido && <WrapItem><Badge colorScheme="orange">teto de ações atingido</Badge></WrapItem>}
      </Wrap>
      {ignorados.length > 0 && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          Deixados de lado: {ignorados.map(([k, n]) => `${num(n)} ${MOTIVOS_IGNORADOS[k] || k}`).join(" · ")}
        </Text>
      )}
      {r.erro && <Text fontSize="xs" color="red.600" mt={1}>{r.erro}</Text>}
    </Box>
  );
}

function ListaAmostra({ titulo, itens, cor }) {
  if (!itens?.length) return null;
  return (
    <Box mt={3}>
      <Text fontSize="xs" fontWeight="bold" color={cor} mb={1}>{titulo}</Text>
      <Box overflowX="auto" borderWidth="1px" borderColor="gray.100" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr><Th>Anúncio</Th><Th>Motivo</Th><Th isNumeric>ROAS</Th><Th isNumeric>Invest.</Th></Tr>
          </Thead>
          <Tbody>
            {itens.map((i) => (
              <Tr key={i.ad_group_id}>
                <Td>
                  <Text fontSize="xs" fontWeight="bold">{i.codigo || i.produto_id || "—"}</Text>
                  <Text fontSize="2xs" color="gray.500" noOfLines={1} maxW="220px">{i.nome} · {i.id_ml}</Text>
                </Td>
                <Td><Text fontSize="xs" minW="160px">{i.motivo}</Text></Td>
                <Td isNumeric fontSize="xs">{roasTxt(i.metricas?.roas)}</Td>
                <Td isNumeric fontSize="xs">{i.metricas ? brl(i.metricas.custo) : "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

function SimulacaoModal({ resultado, carregando, onFechar, opcoes }) {
  const aberto = carregando || !!resultado;
  return (
    <Modal isOpen={aberto} onClose={onFechar} size={{ base: "full", md: "3xl" }} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Prévia da regra</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {carregando && <Center py={10}><Spinner color={ACCENT} /><Text ml={3} fontSize="sm">Consultando o Mercado Livre...</Text></Center>}
          {resultado && (
            <VStack align="stretch" spacing={5}>
              <Text fontSize="sm" color="gray.600">
                Nada foi alterado. É o que a regra faria agora, com o mapeamento de anúncios que já existe.
              </Text>
              {Object.entries(resultado.contas || {}).map(([conta, r]) => (
                <Box key={conta}>
                  <ResumoConta conta={conta} r={r} opcoes={opcoes} />
                  {r.sem_ads > 0 && (
                    <Text fontSize="xs" color="gray.600" mt={2}>
                      Anúncio sem grupo no Ads ainda não pode entrar em campanha: o Mercado Livre cria esse grupo sozinho.
                      A regra procura de novo todo dia.
                    </Text>
                  )}
                  <ListaAmostra titulo={`Entrariam (amostra de ${num(r.amostra_entrar?.length)})`} itens={r.amostra_entrar} cor="green.600" />
                  <ListaAmostra titulo={`Sairiam (amostra de ${num(r.amostra_sair?.length)})`} itens={r.amostra_sair} cor="red.600" />
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onFechar}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function CampoNumero({ label, valor, onChange, prefixo, sufixo, ajuda, placeholder = "desligado", passo = "1" }) {
  return (
    <FormControl>
      <FormLabel fontSize="sm" mb={1}>{label}</FormLabel>
      <InputGroup size="sm">
        {prefixo && <InputLeftAddon>{prefixo}</InputLeftAddon>}
        <Input type="number" step={passo} min={0} value={valor} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} inputMode="decimal" />
        {sufixo && <InputRightAddon>{sufixo}</InputRightAddon>}
      </InputGroup>
      {ajuda && <FormHelperText fontSize="xs" mt={1}>{ajuda}</FormHelperText>}
    </FormControl>
  );
}

function RegraDrawer({ aberto, inicial, opcoes, onFechar, onSalvo, onSimular }) {
  const toast = useToast();
  const [form, setForm] = useState(() => regraVazia(opcoes));
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (aberto) setForm(inicial ? regraParaForm(inicial) : regraVazia(opcoes));
  }, [aberto, inicial, opcoes]);

  const contas = (opcoes?.contas || []).filter((c) => c.anunciante);
  const fornecedores = opcoes?.fornecedores || [];
  const setE = (campo) => (v) => setForm((f) => ({ ...f, criterios_entrada: { ...f.criterios_entrada, [campo]: v } }));
  const setS = (campo) => (v) => setForm((f) => ({ ...f, criterios_saida: { ...f.criterios_saida, [campo]: v } }));
  const alternarFornecedor = (id) => setForm((f) => {
    const atual = f.criterios_entrada.fornecedor_ids || [];
    const novo = atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id];
    return { ...f, criterios_entrada: { ...f.criterios_entrada, fornecedor_ids: novo } };
  });

  const temCampanha = Object.values(form.campanhas).some(Boolean);
  const e = form.criterios_entrada;
  const temCriterio = (e.fornecedor_ids || []).length > 0
    || ["estoque_taga_min", "estoque_fornecedor_min", "preco_min", "preco_max", "margem_min_pct"].some((k) => e[k] !== "");
  const valido = form.nome.trim().length >= 3 && temCampanha && temCriterio;

  const salvar = async () => {
    setSalvando(true);
    try {
      await salvarMlAdsRegra(paraEnvio(form));
      toast({ title: "Regra salva.", status: "success", duration: 3000 });
      onSalvo();
      onFechar();
    } catch (err) {
      toast({ title: "Não foi possível salvar", description: erroMsg(err), status: "error", duration: 8000, isClosable: true });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Drawer isOpen={aberto} placement="right" size={{ base: "full", md: "lg" }} onClose={onFechar}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{form.id ? "Editar regra" : "Nova regra"}</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={5}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Nome</FormLabel>
              <Input size="sm" value={form.nome} maxLength={120} onChange={(ev) => setForm((f) => ({ ...f, nome: ev.target.value }))}
                placeholder="Ex.: BL PARTS" />
            </FormControl>

            <Box>
              <Text fontWeight="bold" fontSize="sm" mb={2}>Campanha de cada conta</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {contas.map((c) => (
                  <FormControl key={c.id}>
                    <FormLabel fontSize="xs" mb={1}>{c.nome}</FormLabel>
                    <Select size="sm" value={form.campanhas[String(c.id)] || ""}
                      onChange={(ev) => setForm((f) => ({ ...f, campanhas: { ...f.campanhas, [String(c.id)]: ev.target.value ? Number(ev.target.value) : null } }))}>
                      <option value="">Não usar nesta conta</option>
                      {(c.campanhas || []).map((cp) => (
                        <option key={cp.id} value={cp.id}>{cp.nome}{cp.status === "paused" ? " (pausada)" : ""}</option>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </SimpleGrid>
              {contas.every((c) => !(c.campanhas || []).length) && (
                <Text fontSize="xs" color="orange.600" mt={2}>Nenhuma campanha ativa ou pausada. Crie uma na aba Campanhas.</Text>
              )}
            </Box>

            <Divider />
            <Box>
              <Text fontWeight="bold" fontSize="sm">Quem entra</Text>
              <Text fontSize="xs" color="gray.500" mb={3}>Anúncio ativo que atende tudo abaixo. Campo vazio não filtra.</Text>
              <FormLabel fontSize="sm" mb={1}>Fornecedores</FormLabel>
              <Wrap spacing={3} mb={3}>
                {fornecedores.map((f) => (
                  <WrapItem key={f.id}>
                    <Checkbox size="sm" isChecked={(e.fornecedor_ids || []).includes(f.id)} onChange={() => alternarFornecedor(f.id)}>
                      <Text fontSize="sm">{f.nome}</Text>
                    </Checkbox>
                  </WrapItem>
                ))}
              </Wrap>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <CampoNumero label="Estoque TAGA mínimo" valor={e.estoque_taga_min} onChange={setE("estoque_taga_min")} sufixo="un."
                  ajuda="1 = só itens com estoque físico na TAGA" placeholder="qualquer" />
                <CampoNumero label="Estoque do fornecedor mínimo" valor={e.estoque_fornecedor_min} onChange={setE("estoque_fornecedor_min")} sufixo="un." placeholder="qualquer" />
                <CampoNumero label="Preço mínimo" valor={e.preco_min} onChange={setE("preco_min")} prefixo="R$" passo="0.01" placeholder="qualquer" />
                <CampoNumero label="Preço máximo" valor={e.preco_max} onChange={setE("preco_max")} prefixo="R$" passo="0.01" placeholder="qualquer" />
                <CampoNumero label="Margem bruta mínima" valor={e.margem_min_pct} onChange={setE("margem_min_pct")} sufixo="%" passo="0.1"
                  ajuda="(preço − custo) ÷ preço, antes de taxa e frete" placeholder="qualquer" />
              </SimpleGrid>
            </Box>

            <Divider />
            <Box>
              <Text fontWeight="bold" fontSize="sm">Quem sai por desempenho</Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Só vale para quem a regra colocou. Campo vazio desliga o critério. Quem sai também quando deixa de atender a entrada (ex.: estoque zerou).
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <CampoNumero label="Sai com ROAS abaixo de" valor={form.criterios_saida.roas_min} onChange={setS("roas_min")} sufixo="x" passo="0.1"
                  ajuda={`Equilíbrio com margem de 30%: ${num(ROAS_EQUILIBRIO, 1)}x`} />
                <CampoNumero label="...depois de pelo menos" valor={form.criterios_saida.min_cliques} onChange={setS("min_cliques")} sufixo="cliques" placeholder="1" />
                <CampoNumero label="Sai com CTR abaixo de" valor={form.criterios_saida.ctr_min_pct} onChange={setS("ctr_min_pct")} sufixo="%" passo="0.05" />
                <CampoNumero label="...depois de pelo menos" valor={form.criterios_saida.min_impressoes_ctr} onChange={setS("min_impressoes_ctr")} sufixo="impr." placeholder="100" />
                <CampoNumero label="Sai se gastar sem nenhuma venda" valor={form.criterios_saida.gasto_sem_venda_max} onChange={setS("gasto_sem_venda_max")} prefixo="R$" passo="1" />
                <CampoNumero label="Janela de avaliação" valor={form.criterios_saida.janela_dias} onChange={setS("janela_dias")} sufixo="dias" placeholder="14"
                  ajuda="O ML atribui venda até 14 dias depois do clique" />
                <CampoNumero label="Carência antes de julgar" valor={form.criterios_saida.carencia_dias} onChange={setS("carencia_dias")} sufixo="dias" placeholder="0" />
                <CampoNumero label="Espera para voltar depois de sair" valor={form.criterios_saida.cooldown_dias} onChange={setS("cooldown_dias")} sufixo="dias" placeholder="0" />
              </SimpleGrid>
            </Box>

            <Divider />
            <Box>
              <Text fontWeight="bold" fontSize="sm" mb={3}>Execução</Text>
              <VStack align="stretch" spacing={3}>
                <Flex justify="space-between" align="center" gap={3}>
                  <Box>
                    <Text fontSize="sm">Regra ligada</Text>
                    <Text fontSize="xs" color="gray.500">Roda todo dia às 12:15 (métricas do ML fecham às 10h).</Text>
                  </Box>
                  <Switch colorScheme="green" isChecked={form.ativa} onChange={(ev) => setForm((f) => ({ ...f, ativa: ev.target.checked }))} />
                </Flex>
                <Flex justify="space-between" align="center" gap={3}>
                  <Box>
                    <Text fontSize="sm">Modo simulação</Text>
                    <Text fontSize="xs" color={form.simulacao ? "gray.500" : "red.600"}>
                      {form.simulacao ? "Só calcula o que faria. Nada muda no ML." : "Coloca e tira anúncios de verdade (gasta dinheiro)."}
                    </Text>
                  </Box>
                  <Switch colorScheme="orange" isChecked={form.simulacao} onChange={(ev) => setForm((f) => ({ ...f, simulacao: ev.target.checked }))} />
                </Flex>
                <CampoNumero label="Prioridade" valor={String(form.prioridade)} onChange={(v) => setForm((f) => ({ ...f, prioridade: v }))}
                  ajuda="Menor número vence quando duas regras querem o mesmo anúncio" placeholder="100" />
              </VStack>
            </Box>
          </VStack>
        </DrawerBody>
        <DrawerFooter gap={2} borderTopWidth="1px" flexWrap="wrap">
          {!valido && (
            <Text fontSize="xs" color="gray.500" w="100%">
              Falta: {[form.nome.trim().length < 3 && "nome", !temCampanha && "campanha", !temCriterio && "um critério de entrada"].filter(Boolean).join(", ")}.
            </Text>
          )}
          <Button size="sm" variant="ghost" onClick={onFechar} isDisabled={salvando}>Cancelar</Button>
          <Button size="sm" variant="outline" leftIcon={<FlaskConical size={14} />} onClick={() => onSimular(paraEnvio(form))} isDisabled={!valido || salvando}>
            Simular
          </Button>
          <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07728" }} onClick={salvar} isLoading={salvando} isDisabled={!valido}>
            Salvar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default function RegrasTab({ opcoes, regras, carregando, onRecarregar }) {
  const toast = useToast();
  const [drawer, setDrawer] = useState({ aberto: false, regra: null });
  const [simulacao, setSimulacao] = useState({ carregando: false, resultado: null });
  const [confirmar, setConfirmar] = useState(null);
  const [executando, setExecutando] = useState(false);
  const fornecedores = useMemo(() => opcoes?.fornecedores || [], [opcoes]);

  const simular = async (regraOuId) => {
    setSimulacao({ carregando: true, resultado: null });
    try {
      const r = await simularMlAdsRegra(regraOuId);
      setSimulacao({ carregando: false, resultado: r });
    } catch (e) {
      setSimulacao({ carregando: false, resultado: null });
      toast({ title: "Não foi possível simular", description: erroMsg(e), status: "error", duration: 8000, isClosable: true });
    }
  };

  const executarConfirmado = async () => {
    if (!confirmar) return;
    setExecutando(true);
    try {
      const r = confirmar.tipo === "excluir" ? await excluirMlAdsRegra(confirmar.regra.id) : await aplicarMlAdsRegra(confirmar.regra.id);
      toast({ title: r?.message || "Feito.", status: "success", duration: 5000, isClosable: true });
      setConfirmar(null);
      onRecarregar();
    } catch (e) {
      toast({ title: "Não foi possível", description: erroMsg(e), status: "error", duration: 8000, isClosable: true });
    } finally {
      setExecutando(false);
    }
  };

  return (
    <Box p={{ base: 3, md: 4 }}>
      <Flex justify="space-between" align="center" gap={2} mb={3} flexWrap="wrap">
        <Text fontSize="sm" color="gray.600" maxW="640px">
          Cada regra cuida de uma campanha por conta: coloca quem atende a entrada e tira quem deixa de atender ou vai mal. Nunca mexe no orçamento.
        </Text>
        <Button size="sm" leftIcon={<Plus size={14} />} bg={ACCENT} color="white" _hover={{ bg: "#e07728" }}
          onClick={() => setDrawer({ aberto: true, regra: null })}>
          Nova regra
        </Button>
      </Flex>

      {carregando && !regras && <Center py={10}><Spinner color={ACCENT} /></Center>}

      {regras && regras.length === 0 && (
        <Box bg="white" borderRadius="lg" boxShadow="sm">
          <Vazio titulo="Nenhuma regra ainda">
            Crie uma regra (ex.: fornecedor BL PARTS) apontando para uma campanha de cada conta. Ela começa em simulação.
          </Vazio>
        </Box>
      )}

      <VStack align="stretch" spacing={3}>
        {(regras || []).map((r) => {
          const resumo = r.ultima_execucao_resumo;
          return (
            <Box key={r.id} bg="white" borderRadius="lg" boxShadow="sm" p={4} borderLeft="4px solid"
              borderLeftColor={!r.ativa ? "gray.300" : r.simulacao ? "orange.300" : "green.400"}>
              <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                <Box minW={0}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontWeight="bold">{r.nome}</Text>
                    <Badge colorScheme={r.ativa ? "green" : "gray"}>{r.ativa ? "Ligada" : "Desligada"}</Badge>
                    {r.simulacao && <Badge colorScheme="orange">Simulação</Badge>}
                    <Badge variant="outline">prioridade {r.prioridade}</Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {Object.entries(r.campanhas || {}).map(([conta, id]) => `Conta ${conta}: ${nomeCampanha(opcoes, conta, id)} (${num(r.na_campanha?.[conta] || 0)} anúncios)`).join(" · ")}
                  </Text>
                </Box>
                <HStack spacing={2} flexWrap="wrap">
                  <Button size="xs" variant="outline" leftIcon={<Pencil size={12} />} onClick={() => setDrawer({ aberto: true, regra: r })}>Editar</Button>
                  <Button size="xs" variant="outline" leftIcon={<FlaskConical size={12} />} onClick={() => simular(r.id)}>Simular</Button>
                  <Button size="xs" colorScheme={r.simulacao ? "orange" : "green"} variant="outline" leftIcon={<Play size={12} />}
                    onClick={() => setConfirmar({ tipo: "aplicar", regra: r })}>Aplicar agora</Button>
                  <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<Trash2 size={12} />} onClick={() => setConfirmar({ tipo: "excluir", regra: r })}>Excluir</Button>
                </HStack>
              </Flex>

              <Wrap spacing={2} mt={3}>
                {chipsEntrada(r.criterios_entrada, fornecedores).map((c) => <WrapItem key={c}><Badge colorScheme="green" variant="subtle" whiteSpace="normal">{c}</Badge></WrapItem>)}
                {chipsSaida(r.criterios_saida).map((c) => <WrapItem key={c}><Badge colorScheme="red" variant="subtle" whiteSpace="normal">{c}</Badge></WrapItem>)}
              </Wrap>

              {resumo ? (
                <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Última execução: {dataHoraBR(r.ultima_execucao_em)}{resumo.simulacao ? " (simulação — nada mudou no ML)" : ""}
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {Object.entries(resumo.contas || {}).map(([conta, rc]) => <ResumoConta key={conta} conta={conta} r={rc} opcoes={opcoes} />)}
                  </VStack>
                </Box>
              ) : (
                <Text fontSize="xs" color="gray.400" mt={3}>Ainda não rodou. Roda sozinha às 12:15 ou pelo "Aplicar agora".</Text>
              )}
            </Box>
          );
        })}
      </VStack>

      <RegraDrawer aberto={drawer.aberto} inicial={drawer.regra} opcoes={opcoes}
        onFechar={() => setDrawer({ aberto: false, regra: null })} onSalvo={onRecarregar} onSimular={simular} />

      <SimulacaoModal resultado={simulacao.resultado} carregando={simulacao.carregando} opcoes={opcoes}
        onFechar={() => setSimulacao({ carregando: false, resultado: null })} />

      <ConfirmDialog
        isOpen={!!confirmar}
        onClose={() => setConfirmar(null)}
        onConfirm={executarConfirmado}
        isLoading={executando}
        title={confirmar?.tipo === "excluir" ? "Excluir regra" : "Aplicar regra agora"}
        confirmText={confirmar?.tipo === "excluir" ? "Excluir" : "Aplicar"}
        confirmColor={confirmar?.tipo === "excluir" ? "red" : "orange"}
        body={confirmar?.tipo === "excluir"
          ? `"${confirmar?.regra.nome}" deixa de rodar. Os anúncios que ela colocou continuam na campanha — pause a campanha se quiser parar o gasto.`
          : confirmar?.regra.simulacao
            ? `"${confirmar?.regra.nome}" está em simulação: vai só recalcular e atualizar o resumo, sem mexer no ML.`
            : `"${confirmar?.regra.nome}" vai colocar e tirar anúncios de verdade na campanha agora.`}
      />
    </Box>
  );
}
